import { HTMLAttributes, useMemo, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Select, TreeSelect, TreeDataNode, message, Tag } from 'antd';
import dayjs, { Dayjs } from 'lib/dayjs';
import QuickRangePicker from 'components/QuickRangePicker';
import useRegionList from 'views/Setting/BlockList/hooks/useRegionList';
import { useTranslate } from '@tolgee/react';
import { platformLabelMap } from 'services/config/platform';
import toLowerCase from 'utils/toLowerCase';
import { getRemindFromHint } from 'views/Conversation/Chat/components/Remind';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useFilterForm } from './FilterForm';
import useScene from '../hooks/useScene';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { useGetRegionLabel } from 'hooks/business/useRegion';

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  .${({ theme }) => theme.getAntdPrefixedClassName('select')} {
    width: 220px;
    margin-right: 15px;
  }
`;

const getRecentDaysRange = (days: number): [Dayjs, Dayjs] => {
  return [
    dayjs()
      .subtract(days - 1, 'day')
      .startOf('day'),
    dayjs().endOf('day'),
  ];
};

export type RemindType = 'view' | 'solve';

export interface DashboardToolbarProps extends HTMLAttributes<HTMLDivElement> {}

function DashboardToolbar(props: DashboardToolbarProps) {
  const { ...otherProps } = props;

  const { t } = useTranslate();
  const getRegionLabel = useGetRegionLabel();

  const systemLanguage = useSystemLanguageValue();

  const {
    values: { platformRegion, scene, startTime, endTime },
    dirty,
    isSubmitting,
    setValues,
    handleSubmit,
  } = useFilterForm();

  const { regions: platformRegionListMap } = useRegionList();

  const { list: sceneList, loading: sceneListLoading, fetchList: fetchSceneList } = useScene();

  const refsCurrent = {
    fetchSceneList,
    t,
    dirty,
    handleSubmit,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const onSubmit = () => {
    setTimeout(() => {
      const {
        current: { dirty, handleSubmit },
      } = refs;

      if (dirty) {
        handleSubmit();
      }
    });
  };

  useEffect(() => {
    const {
      current: { fetchSceneList, t },
    } = refs;

    fetchSceneList().catch(() => {
      message.error(t('remind.网络错误，请稍后再试'));
    });
  }, []);

  return (
    <Container {...otherProps}>
      <Left>
        <TreeSelect
          placeholder={t('remind.选择平台')}
          allowClear
          treeCheckable
          showArrow
          disabled={isSubmitting}
          showCheckedStrategy="SHOW_PARENT"
          maxTagCount="responsive"
          value={platformRegion.reduce<string[]>((res, cur) => {
            const { platform, region: regionList } = cur;

            return [...res, ...regionList.map((region) => `${platform}-${region}`)];
          }, [])}
          onChange={(values) => {
            const map = values.reduce<Record<General.PlatformUpperCase, General.UpperCaseRegion[]>>(
              (res, cur) => {
                if (cur === 'SHOPEE' || cur === 'LAZADA' || isTiKTokUpperCase(cur)) {
                  res[cur] = platformRegionListMap[cur];

                  return res;
                }

                const [platform, region] = cur.split('-');

                const { [platform as General.PlatformUpperCase]: list = [] } = res;

                list.push(region as General.UpperCaseRegion);

                return res;
              },
              {
                SHOPEE: [],
                LAZADA: [],
                TOKOPEDIA: [],
                TIKTOK: [],
                TIKTOK_US: [],
                ALIEXPRESS: [],
                MERCADO: [],
                KWAI: [],
                FACEBOOK: [],
                JOLECT: [],
              },
            );

            const platformRegion = Object.keys(map).map((platform) => {
              const { [platform]: region } = map;

              return {
                platform,
                region,
              };
            });

            setValues((values) => {
              return {
                ...values,
                platformRegion,
                storeId: [],
              };
            });
          }}
          onDropdownVisibleChange={onSubmit}
          onClear={onSubmit}
          treeData={useMemo(() => {
            return Object.keys(platformRegionListMap).reduce<TreeDataNode[]>((res, platform) => {
              if (platform === 'ALIEXPRESS') {
                return res;
              }

              const { [platform]: regionList } = platformRegionListMap;

              if (regionList.length === 0) {
                return res;
              }

              const node = {
                title: platformLabelMap[toLowerCase(platform)],
                key: platform,
                value: platform,
                children: regionList.map((region) => {
                  return {
                    title: getRegionLabel(region),
                    key: `${platform}-${region}`,
                    value: `${platform}-${region}`,
                  };
                }),
              };

              res.push(node);

              return res;
            }, []);
          }, [platformRegionListMap, getRegionLabel])}
          treeNodeFilterProp="title"
          tagRender={(props) => {
            const { onClose, label, ...otherProps } = props;

            return (
              <Tag
                onClose={(e) => {
                  onClose(e);
                  onSubmit();
                }}
                {...otherProps}
              >
                {label}
              </Tag>
            );
          }}
        />
        <Select
          mode="multiple"
          placeholder={t('remind.选择提醒类型')}
          allowClear
          showArrow
          disabled={isSubmitting}
          loading={sceneListLoading}
          maxTagCount="responsive"
          value={scene}
          onChange={(scene) =>
            setValues((values) => {
              return {
                ...values,
                scene,
              };
            })
          }
          onDropdownVisibleChange={onSubmit}
          onClear={onSubmit}
          options={(sceneList || []).map((item) => {
            const { scene, sceneName } = item;

            return {
              value: scene,
              label: getRemindFromHint(sceneName, systemLanguage),
            };
          })}
          tagRender={(props) => {
            const { onClose, label, ...otherProps } = props;

            return (
              <Tag
                onClose={(e) => {
                  onClose(e);
                  onSubmit();
                }}
                {...otherProps}
              >
                {label}
              </Tag>
            );
          }}
        />
      </Left>
      <QuickRangePicker
        allowClear={false}
        disabled={isSubmitting}
        value={[dayjs(startTime), dayjs(endTime)]}
        onChange={(range) => {
          const [start, end] = range || [];

          if (start && end) {
            setValues((values) => {
              return {
                ...values,
                startTime: start.startOf('day').valueOf(),
                endTime: end.endOf('day').valueOf(),
              };
            });
          }
        }}
        onQuickActionClick={onSubmit}
        onOpenChange={onSubmit}
        disabledDate={(date) => {
          const [start, end] = getRecentDaysRange(30);

          return date.isBefore(start) || date.isAfter(end);
        }}
        quickActions={[
          {
            key: 'yesterday',
            label: t('remind.昨天'),
            range: () => {
              return [
                dayjs().subtract(1, 'day').startOf('day'),
                dayjs().subtract(1, 'day').endOf('day'),
              ];
            },
          },
          {
            key: 'last7days',
            label: t('remind.近x天', { x: 7 }),
            range: () => getRecentDaysRange(7),
          },
          {
            key: 'last30days',
            label: t('remind.近x天', { x: 30 }),
            range: () => getRecentDaysRange(30),
          },
        ]}
      />
    </Container>
  );
}

export default DashboardToolbar;
